/**
 * Shared display helpers for the Operator Decision panels.
 * - No raw ISO timestamps in main UI: format to a readable ET string.
 * - No raw numbers without unit context.
 * - Diagnostics may be collapsed under a "Diagnostics" disclosure.
 */

/** "May 16, 5:47 PM ET" with locale-aware fallback if Intl misbehaves. */
export function fmtEtTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d) + ' ET';
  } catch {
    return '—';
  }
}

/** "12m ago" / "3h ago" / "2d ago" / "never". */
export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return 'never';
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return 'never';
    const diffMs = Date.now() - dt.getTime();
    const minutes = Math.round(diffMs / 60000);
    if (Math.abs(minutes) < 1) return 'just now';
    if (Math.abs(minutes) < 60) return `${Math.abs(minutes)}m ago`;
    const hours = Math.round(minutes / 60);
    if (Math.abs(hours) < 48) return `${Math.abs(hours)}h ago`;
    const days = Math.round(hours / 24);
    return `${Math.abs(days)}d ago`;
  } catch {
    return 'never';
  }
}

export function fmtCurrency(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(v);
}

export function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
}

export function titleize(s: string | null | undefined): string {
  if (!s) return '—';
  return s
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Tailwind classes for a generic status pill, given a normalized severity. */
export type Severity = 'ok' | 'monitoring' | 'limited' | 'review' | 'lock' | 'halt' | 'unknown';

export function severityToClass(s: Severity): string {
  switch (s) {
    case 'ok':
      return 'text-emerald-600 bg-emerald-500/10 border-emerald-500/40';
    case 'monitoring':
      return 'text-sky-600 bg-sky-500/10 border-sky-500/40';
    case 'limited':
      return 'text-amber-600 bg-amber-500/10 border-amber-500/40';
    case 'review':
      return 'text-amber-600 bg-amber-500/10 border-amber-500/40';
    case 'lock':
      return 'text-rose-600 bg-rose-500/10 border-rose-500/40';
    case 'halt':
      return 'text-rose-600 bg-rose-500/10 border-rose-500/50';
    case 'unknown':
    default:
      return 'text-[var(--muted-text)] bg-[var(--card-border)]/30 border-[var(--card-border)]';
  }
}

export function severityToLabel(s: Severity): string {
  switch (s) {
    case 'ok':
      return 'Normal';
    case 'monitoring':
      return 'Monitoring';
    case 'limited':
      return 'Limited';
    case 'review':
      return 'Needs Review';
    case 'lock':
      return 'Locked Out';
    case 'halt':
      return 'Halted';
    case 'unknown':
    default:
      return 'Data Unavailable';
  }
}

/** Map a free-form lockout reason code (e.g. "stale_tick", "no_broker") into
 *  a short, human-readable label. Falls back to titleize() when unknown so
 *  unknown reasons still render without raw underscores.
 */
export function humanizeLockoutReason(code: string | null | undefined): string {
  if (!code) return 'Unknown reason';
  const key = String(code).trim().toLowerCase();
  const TABLE: Record<string, string> = {
    stale_tick: 'Stale market tick',
    stale_data: 'Stale data',
    stale_quote: 'Stale quote',
    spread: 'Spread too wide',
    spread_too_wide: 'Spread too wide',
    wide_spread: 'Spread too wide',
    news: 'News window block',
    news_blackout: 'News blackout',
    calendar: 'Economic calendar block',
    economic_calendar: 'Economic calendar block',
    lockout: 'Strategy locked out',
    no_broker: 'Broker not connected',
    no_signal: 'No qualifying signal',
    no_setup: 'No qualifying setup',
    low_atr: 'Volatility too low',
    high_atr: 'Volatility too high',
    rsi_filter: 'RSI filter blocked entry',
    confidence_low: 'Confidence below threshold',
    risk_review: 'Risk review required',
    council_review: 'Council review required',
    daily_loss_cap: 'Daily loss cap reached',
    cooldown: 'Cooldown active',
    market_closed: 'Market closed',
    weekend_close: 'Weekend close window',
  };
  if (TABLE[key]) return TABLE[key];
  return titleize(code);
}

/** Map a lockout severity string into the shared Severity bucket. */
export function lockoutSeverity(s: string | null | undefined): Severity {
  switch (String(s || '').trim().toLowerCase()) {
    case 'critical':
    case 'emergency':
    case 'red':
      return 'halt';
    case 'warning':
    case 'warn':
    case 'amber':
    case 'yellow':
      return 'review';
    case 'info':
    case 'monitoring':
    case 'low':
      return 'monitoring';
    case 'ok':
    case 'green':
    case 'normal':
      return 'ok';
    default:
      return 'unknown';
  }
}
